import { useTranslation } from "react-i18next";
import { ModalPopup } from "../../common";
import AppRoutes from "../../../routes/AppRoutes";

interface AnalyticsPastDataInfoModalProps {
  isOpen: boolean;
  onOpenChange(open: boolean): void;
  /** Opened from the Advanced filters panel, which already sits at z-50. */
  isAboveOverlay?: boolean;
}

/**
 * Caveats for data snapshots (ADR-0006), aligned with the HelpScout article.
 * Opened on demand from the info icon so the filter bar itself stays quiet.
 */
function AnalyticsPastDataInfoModal({
  isOpen,
  onOpenChange,
  isAboveOverlay = false,
}: AnalyticsPastDataInfoModalProps) {
  const { t } = useTranslation();

  const points = [
    t(
      "Analytics.Filters.PastDataInfo.Point1",
      "Pick a date and the charts re-run as the data stood at the end of that day (UTC).",
    ),
    t(
      "Analytics.Filters.PastDataInfo.Point2",
      "Scores, the widgets on the page, and each person's job title, job level, location and active status all follow that date.",
    ),
    t(
      "Analytics.Filters.PastDataInfo.Point3",
      "Who you are allowed to see does not go back in time. Your current analytics access, and your current reporting line if you are a manager, always apply.",
    ),
    t(
      "Analytics.Filters.PastDataInfo.Point4",
      "User groups always use their current rules and members, so editing a group later can change what a saved filter returns.",
    ),
    t(
      "Analytics.Filters.PastDataInfo.Point5",
      "Changes made in your company in the last day or so may not show up yet, and data snapshots are not available on the Usage page.",
    ),
  ];

  return (
    <ModalPopup
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("Analytics.Filters.ShowPastData", "Data snapshot")}
      titleDescription={t(
        "Analytics.Filters.PastDataInfo.Intro",
        "Before you use a data snapshot, it helps to know what follows the date and what always stays current.",
      )}
      width="SMALL"
      alertOpenedWithinModal={isAboveOverlay}
      secondaryButtonText={t("Common.Close", "Close")}
      onSecondaryButtonClick={() => onOpenChange(false)}
    >
      <ul className="list-disc space-y-2 pl-5 text-sm text-gray-700">
        {points.map((point, index) => (
          <li key={index}>{point}</li>
        ))}
      </ul>
      <p className="pt-2 text-sm">
        <a
          href={AppRoutes.helpAnalyticsHistoricalMode}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-gray-700 underline"
        >
          {t(
            "Analytics.Filters.PastDataInfo.LearnMore",
            "Learn more about data snapshots (opens in a new tab)",
          )}
        </a>
      </p>
    </ModalPopup>
  );
}

export default AnalyticsPastDataInfoModal;
